import { compare } from "compare-versions";
import { BaseClient, jsonParseReviver } from "./api-client-extensions";
import { RequestInitWithRetries } from "./monkey-patches";
import _ from "lodash";
import { v7 as uuidv7 } from "uuid";

const urlPrefix = "api/web/v1";
const globals = {
    qTraceId: uuidv7(),
    versionInfo: {
        metastore: "0.6.0",
        datamodel: "0.0.1"
    } as VersionInfo,
    canReachEventsHub: false,
    isInitialized: false,
};

/// Initializes the globals object with the correct version info from the Metastore.
/// This is only called once on startup.
export async function initializeGlobals(metastoreUrl: string) {
    if (globals.isInitialized) return;

    const metadataClient = new MetadataClient(metastoreUrl);

    await Promise.all([
        metadataClient.getVersionInfo(),
        metadataClient.canReachEventsHub()
    ])
    .then(([versionInfo, canReachEventsHub]) => {
        if (versionInfo) globals.versionInfo = versionInfo;
        globals.canReachEventsHub = canReachEventsHub;
        globals.isInitialized = true;
    });
}

export function generateNewTraceId() {
    globals.qTraceId = uuidv7();
}

export const metastoreSupportsCustomHeaders = () => compare(globals.versionInfo.metastore, "0.8.4", ">=");
export const metastoreSupportsStableRedirect = () => compare(globals.versionInfo.metastore, "0.8.6", ">=");
export const metastoreSupportsWebSockets = () => globals.canReachEventsHub;

const getDefaultRequestHeaders = (activeBranch: string) => {
    const result = {
        "Branch": activeBranch,
        "BranchState": "clean",
        "QSC-Data-Model": "0.0.1",
    };

    // Only add the Q-Trace-Id header if the Metastore version is >= 0.8.4.
    // Any older version will raise a CORS error if this header is included.
    if (metastoreSupportsCustomHeaders()) {
        result["Q-Trace-Id"] = globals.qTraceId;
    }

    return result;
};

interface IHttpClient {
    fetch(url: RequestInfo, init?: RequestInit): Promise<Response>;
}

class BaseModel {
    name: string;
    schemaVersion?: string;
    object?: string;
    createdOnMs?: string;
    storedOnMs?: string;
    version?: string;
}

export class MenuItemModel extends BaseModel {
    displayOrder: number;
    title: string;
    icon?: {
        type: "URL" | "BuiltIn" | "Binary";
        content: string;
    };
    description?: string;
    storyline?: string;
    get url() {
        return this.storyline ? `/storylines/${this.storyline}` : null;
    };
    children?: MenuItemModel[];
    get menuLocationId() {
        return 1;
    }
}

export class MenuItemsClient extends BaseClient {
    private http: IHttpClient;
    private baseUrl: string;
    private activeBranch: string;

    constructor(baseUrl: string, activeBranch: string, http?: IHttpClient) {
        super();
        this.http = http ? http : window as any;
        this.activeBranch = activeBranch;
        this.baseUrl = baseUrl;
    }

    static mapToMenuItemModel(item: MenuItemModel) {
        if (!item) return null;

        const result = Object.assign(new MenuItemModel(), item);
        result.children = result.children?.map(MenuItemsClient.mapToMenuItemModel);
        return result;
    }

    getAll(): Promise<MenuItemModel[]> {
        let url_ = `${this.baseUrl}/${urlPrefix}/menu-items`;
        let options_: RequestInitWithRetries = {
            method: "GET",
            headers: {
                "Accept": "application/json",
                ...getDefaultRequestHeaders(this.activeBranch)
            },
            retries: 2,
            timeout: 8000
        };
        return this.http.fetch(url_, options_).then((response: Response) => {
            return parseResponse<MenuItemModel[]>(response).then(result => {
                return result.map(MenuItemsClient.mapToMenuItemModel);
            });
        }).catch(ex => {
            ex["Q-Trace-Id"] = options_.headers["Q-Trace-Id"];
            throw ex;
        });
    }

    getById(id: string): Promise<MenuItemModel> {
        let url_ = `${this.baseUrl}/${urlPrefix}/menu-items/${id}`;
        let options_: RequestInit = {
            method: "GET",
            headers: {
                "Accept": "application/json",
                ...getDefaultRequestHeaders(this.activeBranch)
            },
        };
        return this.http.fetch(url_, options_).then((response: Response) => {
            return parseResponse<MenuItemModel>(response);
        }).catch(ex => {
            ex["Q-Trace-Id"] = options_.headers["Q-Trace-Id"];
            throw ex;
        });
    }
}

export interface Parameter {
    name: string;
    type?: string;
    default?: any;
}

export class Datasource extends BaseModel {
    requiredParameters?: Parameter[];
    optionalParameters?: Parameter[];
    defaultData?: string;
    autoRefresh?: boolean;
    apiEndpoint?: string[];
    allowLazyLoading?: boolean;
}

export class StorylineObjectModel extends BaseModel {
    dataSources?: {
        [key: string]: Datasource;
    };
    isHideable?: boolean;
}

export class Storyline extends StorylineObjectModel {
    friendlyUrl?: string;
    chapters: Chapter[];
    templates?: Record<string, TemplateContent>; // Nullable for backwards-compatibility.  @todo: Remove nullability once all metastore instances are updated.
}

export class Chapter extends StorylineObjectModel {
    pages: Page[];
}

export class Page extends StorylineObjectModel {
    template: Template | string; // Union type for backwards-compatibility.  Will be string in the latest version of the metastore.  @todo: Remove once all metastore instances are updated.
    paragraphs: Paragraph[];
}

export class Paragraph extends StorylineObjectModel {
    slots: Slot[];
}

export class Slot extends BaseModel {
    dataSource?: string;
}

export class TemplateContent {
    jsx: string;
    css: string;
}

export class Template extends BaseModel {
    root: TemplateContent;
}

export class TemplateInputModel {
    id: string;
    jsx: string;
    css: string;
    includes?: TemplateInputModel[];
}

function getFullTemplateHierarchy(template: TemplateInputModel): TemplateInputModel[] {
    const allChildren = _.flatMap(template.includes ?? [], getFullTemplateHierarchy);
    return [template, ...allChildren];
}

export class StorylinesClient extends BaseClient {
    private http: IHttpClient;
    private baseUrl: string;
    private activeBranch: string;

    constructor(baseUrl: string, activeBranch: string, http?: IHttpClient) {
        super();
        this.http = http ? http : window as any;
        this.activeBranch = activeBranch;
        this.baseUrl = baseUrl;
    }

    getAll(): Promise<Storyline[]> {
        let url_ = `${this.baseUrl}/${urlPrefix}/storylines`;
        let options_: RequestInit = {
            method: "GET",
            headers: {
                "Accept": "application/json",
                ...getDefaultRequestHeaders(this.activeBranch)
            },
        };
        return this.http.fetch(url_, options_).then((response: Response) => {
            return parseResponse<Storyline[]>(response);
        }).catch(ex => {
            ex["Q-Trace-Id"] = options_.headers["Q-Trace-Id"];
            throw ex;
        });
    }

    getById(id: string): Promise<Storyline> {
        let url_ = `${this.baseUrl}/${urlPrefix}/storylines/${id}`;
        let options_: RequestInit = {
            method: "GET",
            headers: {
                "Accept": "application/json",
                ...getDefaultRequestHeaders(this.activeBranch)
            },
        };
        return this.http.fetch(url_, options_).then((response: Response) => {
            return parseResponse<Storyline>(response);
        }).catch(ex => {
            ex["Q-Trace-Id"] = options_.headers["Q-Trace-Id"];
            throw ex;
        });
    }

    updateTemplate(storyline_id: string, template: TemplateInputModel): Promise<string> {
        // Backwards compatibility for older version of datamodel - only update the JSX + CSS for a single template at a time...
        if (compare(globals.versionInfo.datamodel, "0.0.2", "<")) {
            const templates = getFullTemplateHierarchy(template);
            return Promise.all(templates.map((t) => this.updateTemplateV1(storyline_id, t))).then(ids => ids.join(","));
        }
        // Latest behaviour - update the template and recursively update all referenced fragments in a single call...
        else {
            return this.updateTemplateV2(storyline_id, template);
        }
    }

    updateTemplateV1(storyline_id: string, template: TemplateInputModel): Promise<string> {
        let url_ = `${this.baseUrl}/${urlPrefix}/storylines/${storyline_id}/template`;
        let options_: RequestInit = {
            method: "PUT",
            body: JSON.stringify({...template, includes: undefined }),
            headers: {
                "Content-Type": "application/json",
                ...getDefaultRequestHeaders(this.activeBranch)
            },
        };
        return this.http.fetch(url_, options_).then((response: Response) => {
            return parseResponse<string>(response);
        }).catch(ex => {
            ex["Q-Trace-Id"] = options_.headers["Q-Trace-Id"];
            throw ex;
        });
    }

    updateTemplateV2(storyline_id: string, template: TemplateInputModel): Promise<string> {
        let url_ = `${this.baseUrl}/${urlPrefix}/storylines/${storyline_id}/template`;
        let options_: RequestInit = {
            method: "PUT",
            body: JSON.stringify(template),
            headers: {
                "Content-Type": "application/json",
                ...getDefaultRequestHeaders(this.activeBranch)
            },
        };
        return this.http.fetch(url_, options_).then((response: Response) => {
            return parseResponse<string>(response);
        }).catch(ex => {
            ex["Q-Trace-Id"] = options_.headers["Q-Trace-Id"];
            throw ex;
        });
    }
}

const compressBody = (body: string) => {
    const parameterStream = new Blob([body], { type: "application/json" }).stream();
    return new Response(parameterStream.pipeThrough(new CompressionStream("gzip"))).blob();
};

export class DatasourcesClient extends BaseClient {
    private http: IHttpClient;
    private baseUrl: string;
    private activeBranch: string;
    private maxDatasourceRetryCount: number | null;
    private dynamicDataApiBypassesMetastore: boolean;

    constructor(baseUrl: string, activeBranch: string, maxDatasourceRetryCount?: number, dynamicDataApiBypassesMetastore?: boolean, http?: IHttpClient) {
        super();
        this.http = http ? http : window as any;
        this.activeBranch = activeBranch;
        this.baseUrl = baseUrl;
        this.maxDatasourceRetryCount = maxDatasourceRetryCount;
        this.dynamicDataApiBypassesMetastore = dynamicDataApiBypassesMetastore;
    }

    async fetchData(apiUrl: string, apiEndpoint: string, parameterValues: object, compressRequestBody: boolean): Promise<any> {
        return this.dynamicDataApiBypassesMetastore ?
            this.fetchDataDirectly(apiUrl, apiEndpoint, parameterValues) :
            this.fetchDataViaMetastore(apiUrl, apiEndpoint, parameterValues, compressRequestBody);
    }

    private async fetchDataViaMetastore(apiUrl: string, apiEndpoint: string, parameterValues: object, compressRequestBody: boolean) {
        let url_ = `${this.baseUrl}/${urlPrefix}/data-sources/data?url=${apiUrl}&endpoint=${apiEndpoint}`;

        const payload = JSON.stringify(parameterValues);
        // Only add the compression additional headers if the Metastore version is >= 0.8.4.
        // Any older version will raise a CORS error if this header is included.
        const [body, additionalHeaders] = compressRequestBody && metastoreSupportsCustomHeaders() ? 
            [await compressBody(payload), { "Content-Type": "application/octet-stream", "Content-Encoding": "gzip" }] : 
            [payload, {}];

        let options_ = {
            method: "POST",
            body: body,
            headers: {
                "Accept": "application/json",
                ...getDefaultRequestHeaders(this.activeBranch),
                ...additionalHeaders,
            },
            retries: this.maxDatasourceRetryCount,
        } as RequestInitWithRetries;

        return this.http.fetch(url_, options_).then((response: Response) => {
            return parseResponse<any>(response);
        }).catch(ex => {
            ex["Q-Trace-Id"] = options_.headers["Q-Trace-Id"];
            throw ex;
        });
    }

    private async fetchDataDirectly(apiUrl: string, apiEndpoint: string, parameterValues: object) {
        let url_ = apiUrl.includes("{endpoint}") ? apiUrl.replace("{endpoint}", apiEndpoint) : `${apiUrl.replace(/\/$/, "")}/${apiEndpoint}`;

        const body = JSON.stringify(parameterValues);
        let options_ = {
            method: "POST",
            body: body,
            headers: {
                "Accept": "application/json",
            },
            retries: this.maxDatasourceRetryCount,
        } as RequestInitWithRetries;

        return this.http.fetch(url_, options_).then((response: Response) => {
            return parseResponse<any>(response);
        });
    }
}

function parseResponse<TModel>(response: Response): Promise<TModel> {
    const status = response.status;
    let _headers: any = {}; if (response.headers && response.headers.forEach) { response.headers.forEach((v: any, k: any) => _headers[k] = v); };
    if (status === 200) {
        return response.text().then((_responseText) => {
            let result200: TModel = null;
            result200 = _responseText === "" ? null : JSON.parse(_responseText, jsonParseReviver) as TModel;
            return result200;
        });
    } else if (status !== 200 && status !== 204) {
        return response.text().then((_responseText) => {
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
        });
    }
}

export class TemplatesClient extends BaseClient {
    private http: IHttpClient;
    private baseUrl: string;
    private activeBranch: string;

    constructor(baseUrl: string, activeBranch: string, http?: IHttpClient) {
        super();
        this.http = http ? http : window as any;
        this.activeBranch = activeBranch;
        this.baseUrl = baseUrl;
    }

    getById(id: string): Promise<Template> {
        let url_ = `${this.baseUrl}/${urlPrefix}/templates/${id}`;
        let options_: RequestInit = {
            method: "GET",
            headers: {
                "Accept": "application/json",
                ...getDefaultRequestHeaders(this.activeBranch)
            },
        };
        return this.http.fetch(url_, options_).then((response: Response) => {
            return parseResponse<Template>(response);
        });
    }
}

export interface VersionInfo {
    metastore: string;
    datamodel: string;
}

class MetadataClient extends BaseClient {
    private http: IHttpClient;
    private baseUrl: string;

    constructor(baseUrl: string, http?: IHttpClient) {
        super();
        this.http = http ? http : window as any;
        this.baseUrl = baseUrl;
    }

    getVersionInfo(): Promise<VersionInfo> {
        let url_ = `${this.baseUrl}/${urlPrefix}/version`;
        let options_: RequestInit = {
            method: "GET",
            headers: {
                "Accept": "application/json",
            }
        };
        return this.http.fetch(url_, options_).then((response: Response) => {
            // This endpoint didn't exist in Metastore < 0.7.0, so we need to handle a 404 response gracefully...
            if (response.status === 200) {
                return parseResponse<VersionInfo>(response);
            }
        }).catch(ex => {
            console.error(ex);
            return undefined;
        });
    }

    canReachEventsHub(): Promise<boolean> {
        const url_ = `${this.baseUrl}/hubs/events/negotiate`;
        const options_: RequestInitWithRetries = {
            method: "POST",
            headers: {
                "Accept": "application/json",
            },
            retries: 0,
            timeout: 1000,
        };

        return this.http.fetch(url_, options_)
            .then((response: Response) => response.status === 200 ? true : false)
            .catch(() => false);
    }
}

export class ApiException extends Error {
    override message: string;
    status: number;
    response: string;
    headers: { [key: string]: any; };
    result: any;

    constructor(message: string, status: number, response: string, headers: { [key: string]: any; }, result: any) {
        super();

        this.message = message;
        this.status = status;
        this.response = response;
        this.headers = headers;
        this.result = result;
    }

    protected isApiException = true;

    static isApiException(obj: any): obj is ApiException {
        return obj.isApiException === true;
    }
}

function throwException(message: string, status: number, response: string, headers: { [key: string]: any; }, result?: any): any {
    if (result !== null && result !== undefined)
        throw result;
    else
        throw new ApiException(message, status, response, headers, null);
}